import {
  FC,
  memo,
  useRef,
  useState,
  Children,
  useEffect,
  useCallback,
  cloneElement,
  isValidElement,
  DialogHTMLAttributes,
} from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { ReactComponent as CloseSVG } from '../../assets/images/icons/close.svg';
import useHideBodyScroll from "../../hooks/useHideBodyScroll";
import useScrollBoundaryGuard from "../../hooks/useScrollBoundaryGuard";
import {hideModal} from "../../store/Modal/Modal.action";
import {Button} from "../Button";
import { ButtonSquare } from '../Button/ButtonSquare';

type PropTypes = DialogHTMLAttributes<HTMLDialogElement> & {
  title?: string;
  isClose?: boolean;
  isCloseBg?: boolean;
  isVisible: boolean;
  onClose?: () => void;
};

const Modal: FC<PropTypes> = ({
  isVisible,
  className,
  children,
  isClose,
  isCloseBg,
  title,
  onClose,
  ...props
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const pointerDownCoordsRef = useRef<{ x: number; y: number } | null>(null);
  const dispatch = useDispatch();
  const [isShow, setShow] = useState(false);
  const isPointerDownOnBackdropRef = useRef(false);

  useHideBodyScroll(isShow);
  useScrollBoundaryGuard(
    ref as unknown as React.RefObject<HTMLElement>,
    isShow,
    {
      preventBackdropScroll: true,
    },
  );

  const handleClose = useCallback(() => {
    if (!isCloseBg && !isClose) return;
    ref?.current?.close();
    dispatch(hideModal());
    if (onClose) onClose();
  }, [dispatch, onClose, isCloseBg, isClose]);

  // for take onClose to child component
  const clonedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { onClose: handleClose } as Partial<unknown>);
    }
    return child;
  });

  // to remove or add scroll on page
  useEffect(() => {
    if (!!ref.current) {
      if (isShow) {
        ref?.current?.showModal();
      } else {
        ref?.current?.close();
      }
    }
  }, [isShow]);

  // click on backdrop with drag guard
  useEffect(() => {
    const dialog = ref?.current;
    if (!dialog) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = (event as any).target as EventTarget | null;
      isPointerDownOnBackdropRef.current = target === dialog;

      if (event instanceof MouseEvent) {
        pointerDownCoordsRef.current = { x: event.clientX, y: event.clientY };
      } else {
        const t =
          (event as TouchEvent).touches?.[0] ||
          (event as TouchEvent).changedTouches?.[0];
        if (t) pointerDownCoordsRef.current = { x: t.clientX, y: t.clientY };
      }
    };

    const handlePointerUp = () => {
      setTimeout(() => {
        isPointerDownOnBackdropRef.current = false;
        pointerDownCoordsRef.current = null;
      }, 0);
    };

    const handleBackdropClick = (event: MouseEvent) => {
      if (!isPointerDownOnBackdropRef.current) return;
      const rect = dialog.getBoundingClientRect();
      const coords = pointerDownCoordsRef.current || {
        x: event.clientX,
        y: event.clientY,
      };
      const x = coords.x;
      const y = coords.y;
      const isInsideDialog =
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      if (!isInsideDialog) {
        handleClose();
      }
    };

    dialog.addEventListener('mousedown', handlePointerDown);
    dialog.addEventListener('touchstart', handlePointerDown as EventListener);
    dialog.addEventListener('mouseup', handlePointerUp);
    dialog.addEventListener('touchend', handlePointerUp as EventListener);
    dialog.addEventListener('click', handleBackdropClick);

    return () => {
      dialog.removeEventListener('mousedown', handlePointerDown);
      dialog.removeEventListener(
        'touchstart',
        handlePointerDown as EventListener,
      );
      dialog.removeEventListener('mouseup', handlePointerUp);
      dialog.removeEventListener('touchend', handlePointerUp as EventListener);
      dialog.removeEventListener('click', handleBackdropClick);
    };
  }, [ref?.current, handleClose]);

  // for animation
  useEffect(() => {
    setShow(isVisible);
  }, [isVisible]);

  // To add or remove correct padding if we have scroll (works only when page load)
  useEffect(() => {
    function handleChangeSize() {
      if (
        (ref?.current?.offsetHeight || 0) < (ref.current?.scrollHeight || 0)
      ) {
        ref.current?.classList.add('with-scroll');
      } else {
        ref.current?.classList.remove('with-scroll');
      }
    }

    handleChangeSize();
    window.addEventListener('resize', handleChangeSize);

    return () => {
      window.removeEventListener('resize', handleChangeSize);
    };
  }, [ref?.current?.offsetHeight, ref.current?.scrollHeight]);

  return (
    <div className={classNames('modal', className)}>
      <dialog ref={ref} {...props} onCancel={handleClose}>
        <div className="modal-headline">
          {!!title && <div className="modal-title">{title}</div>}

          {isClose && (
              <ButtonSquare variant="secondary" className="modal-close" onClick={handleClose}>
                <CloseSVG />
              </ButtonSquare>
          )}
        </div>

        {clonedChildren}
      </dialog>
    </div>
  );
};

export default memo(Modal);
